import ProductService from "@/services/product-service";

/* ── The payment in flight ────────────────────────────────────────────────
   `selectedProduct` lives only in memory, and every payment provider sends the
   customer back with a full page load — so a cancelled Stripe checkout used to
   return to a panel that had forgotten which package was being bought. What
   the customer left to pay for is written to sessionStorage the moment they
   leave for the provider.

   It is read back in exactly ONE situation: a return through the `/cancel`
   route, which is Stripe saying "this customer backed out". Nothing else ever
   restores it — not a reload, not a later visit to the payment page — because
   the record describes that one departure and nothing after it: a package
   picked since, or a payment that went through while the customer was signed
   out, would otherwise be overruled by a package they are no longer buying.
   Choosing a package (`setSelectedProduct`) and reaching /success both clear
   it, and reading it back consumes it.

   sessionStorage, not localStorage: the provider returns to the same tab, and
   a record that outlived the tab would resurrect an old purchase in a new one.
   Every access fails soft — private modes and blocked storage throw, and a
   missing convenience must never break the payment page.

   Deliberately NOT cleared on sign-out, unlike the cart: a session that
   expires while the customer is on the provider's page signs them out on the
   way back, and putting their package back after they sign in again is the
   case this exists for. It names a catalogue package, nothing personal.

   Shape: { kind: 'package' | 'plan', productId, from, startedAt, cancelledAt? } */
const PENDING_KEY = "pending_payment";

/* Stripe closes a Checkout Session 24 hours after creating it, so a return to
   /cancel later than that cannot be about the departure on record. */
const PENDING_LIFETIME_MS = 24 * 60 * 60 * 1000;

/* How long "Payment cancelled — nothing was charged" stays worth saying. Long
   enough to survive a sign-in on the way back, short enough that it cannot turn
   up hours later about a checkout nobody remembers. */
const CANCELLED_NOTICE_MS = 30 * 60 * 1000;

const readPending = () => {
  try {
    const raw = JSON.parse(sessionStorage.getItem(PENDING_KEY));
    return raw && typeof raw === "object" ? raw : null;
  } catch {
    return null;
  }
};

const writePending = (value) => {
  try {
    if (value) sessionStorage.setItem(PENDING_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // Storage unavailable: the return trip simply won't be able to restore.
  }
};

/** Call as the customer leaves for the provider. `from` is where a plan was picked. */
export function rememberPendingPayment({ kind, productId, from = null }) {
  writePending({ kind, productId: productId ?? null, from, startedAt: Date.now() });
}

/** The payment went through, or a new package was chosen: nothing to restore. */
export function forgetPendingPayment() {
  writePending(null);
}

/*
 * The target of the `/cancel` route, which is where Stripe sends a customer who
 * backs out of checkout. Called from a route `redirect`, which vue-router runs
 * BEFORE the auth guard — so the notice is recorded even when the guard then
 * sends a signed-out customer to /login, and the billing page they reach after
 * signing in can still say what happened.
 *
 * `cancelledAt` is the marker that makes the record restorable at all; see
 * `takeCancelledNotice`.
 */
export function paymentCancelledRedirect() {
  const stored = readPending();
  const pending =
    stored && Date.now() - Number(stored.startedAt || 0) < PENDING_LIFETIME_MS ? stored : {};
  writePending({ ...pending, cancelledAt: Date.now() });
  if (pending.kind === "package" && pending.productId != null) return "/replenish";
  if (pending.kind === "plan" && pending.from) return pending.from;
  return "/products";
}

/*
 * One-shot, and the only way the record is ever read back. After a return
 * through /cancel, the first billing page that `accepts` the kind of payment
 * that was cancelled gets `{ kind, productId, from }` (kind null when nothing
 * was pending), and the record is deleted in the same step — so the strip
 * reports the event once, and the package can be put back once. Pages that do
 * not accept the kind (the subscriptions page, for a package) leave it for the
 * page that does. Null in every other case.
 */
export function takeCancelledNotice(accepts = () => true) {
  const pending = readPending();
  if (!pending?.cancelledAt) return null;
  if (Date.now() - pending.cancelledAt >= CANCELLED_NOTICE_MS) {
    writePending(null);
    return null;
  }
  const kind = pending.kind ?? null;
  if (!accepts(kind)) return null;
  writePending(null);
  return { kind, productId: pending.productId ?? null, from: pending.from ?? null };
}

const initialState = { deals: undefined, dealsForUser: undefined, selectedProduct: undefined };

export const products = {
  namespaced: true,
  state: initialState,
  actions: {
    /* A package chosen by the customer replaces whatever a cancelled checkout
       left on record: the next /cancel must be about THIS one, or nothing. */
    setSelectedProduct({ commit }, payload = null) {
      forgetPendingPayment()
      commit("setSelectedProduct", payload)
    },
    getTokenProducts({ commit }) {
      const products = JSON.parse(localStorage.getItem("products"));
      if (products) {
        commit("getAllTokenProducts", products)
      }

      return ProductService.getAllProducts().then(
        (products) => {
          commit("getAllTokenProducts", products.data);
          localStorage.setItem("products", JSON.stringify(products.data));
          return Promise.resolve(products.data);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getTokenProductsByUser({commit}) {
      const productsByUser = JSON.parse(localStorage.getItem("productsByUser"));
      if (productsByUser) {
        commit("setAllTokenProductsByUser", productsByUser)
      }

      return ProductService.getAllProductsByUser().then(
        (productsByUser) => {
          commit("setAllTokenProductsByUser", productsByUser.data)
          localStorage.setItem("productsByUser", JSON.stringify(productsByUser.data))
          return Promise.resolve(productsByUser.data)
        },
        (error) => {
          return Promise.reject(error)
        })
    },

    /*
     * Puts the package a payment was started for back into `selectedProduct`,
     * after the provider's full-page return wiped memory. Looked up in the
     * catalogue rather than stored whole, so the payment page shows today's
     * price, never a figure saved before the customer left. Personal deals are
     * searched too — they are bought through the same page.
     *
     * Resolves to the product, or null when it can't be found (withdrawn, or
     * the catalogue is unreachable). Never rejects: the caller's fallback is
     * the page's own "No package selected" state.
     */
    async restoreSelectedProduct({ commit, dispatch, state }, productId) {
      const find = () =>
        [...(state.deals || []), ...(state.productsByUser || [])].find(
          (product) => String(product?.id) === String(productId)
        ) ?? null;

      let product = find();
      if (!product) {
        await Promise.allSettled([
          dispatch("getTokenProducts"),
          dispatch("getTokenProductsByUser"),
        ]);
        product = find();
      }
      if (product) commit("setSelectedProduct", product);
      return product;
    },
  },
  mutations: {
    setSelectedProduct(state, product) {
      state.selectedProduct = product
    },
    getAllTokenProducts(state, products) {
      state.deals = products;
    },
    setAllTokenProductsByUser(state, products) {
      state.productsByUser = products;
    }
  },
};
